/**
 * Calendar & Diary wave 6a — public booking page (Calendly-style, §3.6).
 *
 * Session-free: the booking-link token IS the credential, so all three
 * endpoints are called with plain fetch (never the authed api client).
 * Fail-closed contract: an unknown / disabled / NULL token is
 * indistinguishable (404 BOOKING_LINK_INVALID) → one honest
 * "not available" state for all of them.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { AlertTriangle, CalendarClock, CheckCircle2, Globe, Loader2 } from 'lucide-react'
import { API_BASE } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useLocale } from '@/hooks/useLocale'
import { usePageTitle } from '@/lib/usePageTitle'
import { cn } from '@/lib/utils'
import { bt, btf, type BookingCopyKey, type BookingLocale } from '@/pages/public/bookingCopy'

type BookingMeta = {
  owner_name: string
  agency_name: string
  timezone: string
  durations: number[]
}

type Slot = { starts_at: string; ends_at: string }

/** One selectable day, expressed as a date in the OWNER's timezone. */
type Day = { key: string; year: number; month: number; day: number }

type PageState =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'error' }
  | { kind: 'ready'; meta: BookingMeta }

type SlotsState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; slots: Slot[] }
  | { kind: 'error' }

const WINDOW_DAYS = 14
const POSTABLE_DURATIONS = [30, 60]
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// Mirrors the backend's E.164-ish check (public-booking-routes.js).
const PHONE_RE = /^\+[1-9]\d{7,14}$/

/* ------------------------------------------------------------------ */
/* Owner-timezone date math (no new deps — follow-up if a tz library   */
/* ever lands).                                                        */
/* ------------------------------------------------------------------ */

type WallParts = { year: number; month: number; day: number; hour: number; minute: number; second: number }

const PARTS_FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>()

function partsFormatter(timezone: string): Intl.DateTimeFormat {
  let fmt = PARTS_FORMATTER_CACHE.get(timezone)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
    PARTS_FORMATTER_CACHE.set(timezone, fmt)
  }
  return fmt
}

function zonedWallParts(instantMs: number, timezone: string): WallParts {
  const parts = partsFormatter(timezone).formatToParts(new Date(instantMs))
  const grab = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0)
  return { year: grab('year'), month: grab('month'), day: grab('day'), hour: grab('hour'), minute: grab('minute'), second: grab('second') }
}

/** Instant (ms) of local midnight Y-M-D in `timezone`. */
function zonedMidnightUtc(year: number, month: number, day: number, timezone: string): number {
  const guess = Date.UTC(year, month - 1, day)
  // Probe at local-ish midday so the offset read is never taken on a DST
  // transition boundary; offsets never shift by hours within half a day.
  const probe = guess + 12 * 60 * 60 * 1000
  const wall = zonedWallParts(probe, timezone)
  const offsetMs = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second) - probe
  return guess - offsetMs
}

/** "Today" as an owner-local calendar date. */
function ownerLocalToday(timezone: string): { year: number; month: number; day: number } {
  const wall = zonedWallParts(Date.now(), timezone)
  return { year: wall.year, month: wall.month, day: wall.day }
}

function buildDays(timezone: string, count: number): Day[] {
  const start = ownerLocalToday(timezone)
  const days: Day[] = []
  const cursor = new Date(Date.UTC(start.year, start.month - 1, start.day))
  for (let i = 0; i < count; i += 1) {
    days.push({
      key: `${cursor.getUTCFullYear()}-${String(cursor.getUTCMonth() + 1).padStart(2, '0')}-${String(cursor.getUTCDate()).padStart(2, '0')}`,
      year: cursor.getUTCFullYear(),
      month: cursor.getUTCMonth() + 1,
      day: cursor.getUTCDate(),
    })
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }
  return days
}

/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

function intlLocale(locale: BookingLocale): string {
  return locale === 'ar' ? 'ar' : 'en-US'
}

// Architect decision
// Architect decision (wave 6a, single-market launch): every time on this
// page renders in the OWNER's timezone and we assume the visitor shares
// it. Converting to the visitor's browser timezone is a deliberate
// follow-up, not an oversight — do not "fix" locally without the spec.
function formatTime(iso: string, timezone: string, locale: BookingLocale): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

function formatDayName(day: Day, timezone: string, locale: BookingLocale): string {
  const instant = zonedMidnightUtc(day.year, day.month, day.day, timezone) + 12 * 60 * 60 * 1000
  return new Intl.DateTimeFormat(intlLocale(locale), { timeZone: timezone, weekday: 'short' }).format(new Date(instant))
}

function formatDayNumber(day: Day, timezone: string, locale: BookingLocale): string {
  const instant = zonedMidnightUtc(day.year, day.month, day.day, timezone) + 12 * 60 * 60 * 1000
  return new Intl.DateTimeFormat(intlLocale(locale), { timeZone: timezone, day: 'numeric' }).format(new Date(instant))
}

function formatFullDayLabel(day: Day, timezone: string, locale: BookingLocale): string {
  const instant = zonedMidnightUtc(day.year, day.month, day.day, timezone) + 12 * 60 * 60 * 1000
  return new Intl.DateTimeFormat(intlLocale(locale), {
    timeZone: timezone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date(instant))
}

/** Long form for the confirmation panel: "Tue, June 16, 3:30–4:00 pm". */
function formatSlotRange(slot: Slot, timezone: string, locale: BookingLocale): string {
  const start = new Date(slot.starts_at)
  const day = new Intl.DateTimeFormat(intlLocale(locale), {
    timeZone: timezone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(start)
  return `${day} · ${formatTime(slot.starts_at, timezone, locale)} – ${formatTime(slot.ends_at, timezone, locale)}`
}

/* ------------------------------------------------------------------ */
/* Fetch plumbing (plain fetch — anonymous endpoints)                  */
/* ------------------------------------------------------------------ */

type FetchOutcome = { status: number; data: unknown }

async function publicFetch(path: string, init?: RequestInit): Promise<FetchOutcome> {
  const res = await fetch(`${API_BASE}${path}`, init)
  let data: unknown = null
  try {
    data = await res.json()
  } catch {
    data = null
  }
  return { status: res.status, data }
}

function isBookingMeta(value: unknown): value is BookingMeta {
  const v = value as BookingMeta | null
  return Boolean(
    v &&
      typeof v.owner_name === 'string' &&
      typeof v.agency_name === 'string' &&
      typeof v.timezone === 'string' &&
      Array.isArray(v.durations),
  )
}

function extractSlots(value: unknown): Slot[] {
  const v = value as { slots?: unknown } | null
  if (!v || !Array.isArray(v.slots)) return []
  return v.slots
    .filter((s): s is Slot => Boolean(s && typeof (s as Slot).starts_at === 'string' && typeof (s as Slot).ends_at === 'string'))
    .map((s) => ({ starts_at: s.starts_at, ends_at: s.ends_at }))
}

type BookedEvent = { id: string; starts_at: string; ends_at: string; status: string }

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export function PublicBookingPage() {
  const { token: rawToken } = useParams<{ token: string }>()
  const token = useMemo(() => String(rawToken || '').trim(), [rawToken])
  const { isArabic } = useLocale()
  const locale: BookingLocale = isArabic ? 'ar' : 'en'
  usePageTitle(bt('page.title', locale))

  const [page, setPage] = useState<PageState>({ kind: 'loading' })
  const [days, setDays] = useState<Day[]>([])
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null)
  const [duration, setDuration] = useState<number>(30)
  const [slots, setSlots] = useState<SlotsState>({ kind: 'idle' })
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [notes, setNotes] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'name' | 'email' | 'phone' | 'notes', string>>>({})
  const [formError, setFormError] = useState<{ key: BookingCopyKey } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [booked, setBooked] = useState<Slot | null>(null)

  // Guards against out-of-order availability responses (slow day A
  // landing after the visitor already picked day B).
  const availabilitySeq = useRef(0)
  const timezoneRef = useRef('UTC')

  /* Metadata load. */
  useEffect(() => {
    let cancelled = false
    if (!token) {
      setPage({ kind: 'unavailable' })
      return
    }
    setPage({ kind: 'loading' })
    publicFetch(`/public/booking/${encodeURIComponent(token)}`)
      .then(({ status, data }) => {
        if (cancelled) return
        if (status === 404) {
          setPage({ kind: 'unavailable' })
          return
        }
        if (status !== 200 || !isBookingMeta(data)) {
          setPage({ kind: 'error' })
          return
        }
        timezoneRef.current = data.timezone
        setDays(buildDays(data.timezone, WINDOW_DAYS))
        const bookable = POSTABLE_DURATIONS.filter((d) => data.durations.includes(d))
        setDuration(bookable[0] ?? 30)
        setPage({ kind: 'ready', meta: data })
        const firstDay = buildDays(data.timezone, WINDOW_DAYS)[0]
        setSelectedDayKey(firstDay.key)
      })
      .catch(() => {
        if (!cancelled) setPage({ kind: 'unavailable' })
      })
    return () => {
      cancelled = true
    }
  }, [token])

  /* Availability load for the selected day + duration. */
  const loadSlots = useCallback(
    (day: Day, durationMinutes: number) => {
      const timezone = timezoneRef.current
      const fromMs = zonedMidnightUtc(day.year, day.month, day.day, timezone)
      const next = new Date(Date.UTC(day.year, day.month - 1, day.day))
      next.setUTCDate(next.getUTCDate() + 1)
      const toMs = zonedMidnightUtc(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate(), timezone)
      const seq = (availabilitySeq.current += 1)
      setSlots({ kind: 'loading' })
      setSelectedSlot(null)
      const query = new URLSearchParams({
        from: new Date(fromMs).toISOString(),
        to: new Date(toMs).toISOString(),
        duration_minutes: String(durationMinutes),
      })
      publicFetch(`/public/booking/${encodeURIComponent(token)}/availability?${query.toString()}`)
        .then(({ status, data }) => {
          if (availabilitySeq.current !== seq) return
          if (status === 200) {
            setSlots({ kind: 'ready', slots: extractSlots(data) })
            return
          }
          if (status === 404) {
            setPage({ kind: 'unavailable' })
            return
          }
          setSlots({ kind: 'error' })
        })
        .catch(() => {
          if (availabilitySeq.current === seq) setSlots({ kind: 'error' })
        })
    },
    [token],
  )

  const readyMeta = page.kind === 'ready' ? page.meta : null

  useEffect(() => {
    if (!readyMeta || !selectedDayKey) return
    const day = days.find((d) => d.key === selectedDayKey)
    if (day) loadSlots(day, duration)
  }, [readyMeta, selectedDayKey, duration, days, loadSlots])

  const pickDuration = (next: number) => {
    setDuration(next)
  }

  const pickDay = (key: string) => {
    if (key !== selectedDayKey) setSelectedSlot(null)
    setSelectedDayKey(key)
  }

  const validate = (): boolean => {
    const errors: typeof fieldErrors = {}
    const trimmedName = name.trim()
    const trimmedEmail = email.trim()
    const trimmedPhone = phone.trim()
    if (!trimmedName) errors.name = bt('form.name.required', locale)
    if (!trimmedEmail) errors.email = bt('form.email.required', locale)
    else if (!EMAIL_RE.test(trimmedEmail)) errors.email = bt('form.email.invalid', locale)
    if (trimmedPhone && !PHONE_RE.test(trimmedPhone)) errors.phone = bt('form.phone.invalid', locale)
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const submit = async () => {
    if (!readyMeta || !selectedSlot || submitting) return
    setFormError(null)
    if (!validate()) {
      setFormError({ key: 'error.invalid' })
      return
    }
    setSubmitting(true)
    try {
      const { status, data } = await publicFetch(`/public/booking/${encodeURIComponent(token)}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          starts_at: selectedSlot.starts_at,
          duration_minutes: duration,
          visitor: {
            name: name.trim(),
            email: email.trim(),
            ...(phone.trim() ? { phone: phone.trim() } : {}),
            ...(notes.trim() ? { notes: notes.trim() } : {}),
          },
        }),
      })
      if (status === 201) {
        const event = (data as { event?: BookedEvent } | null)?.event
        if (event && typeof event.starts_at === 'string' && typeof event.ends_at === 'string') {
          setBooked({ starts_at: event.starts_at, ends_at: event.ends_at })
        } else {
          setBooked(selectedSlot)
        }
        return
      }
      if (status === 409) {
        // Engine denial (e.g. CALENDAR_CONFLICT) — the slot disappeared
        // under the visitor. Re-pick honestly; never surface the raw code.
        setFormError({ key: 'error.taken' })
        setSelectedSlot(null)
        const day = days.find((d) => d.key === selectedDayKey)
        if (day) loadSlots(day, duration)
        return
      }
      if (status === 429) {
        setFormError({ key: 'error.rateLimited' })
        return
      }
      if (status === 400) {
        const details = (data as { details?: Array<{ path?: Array<string | number>; message?: string }> } | null)?.details
        const mapped: typeof fieldErrors = {}
        let sawMapped = false
        for (const issue of Array.isArray(details) ? details : []) {
          const field = issue.path?.[issue.path.length - 1]
          if (field === 'name' || field === 'email' || field === 'phone' || field === 'notes') {
            mapped[field] = typeof issue.message === 'string' ? issue.message : bt('error.invalid', locale)
            sawMapped = true
          }
        }
        if (sawMapped) {
          setFieldErrors((prev) => ({ ...prev, ...mapped }))
          setFormError({ key: 'error.invalid' })
        } else {
          setFormError({ key: 'error.submit' })
        }
        return
      }
      setFormError({ key: 'error.submit' })
    } catch {
      setFormError({ key: 'error.submit' })
    } finally {
      setSubmitting(false)
    }
  }

  /* ------------------------------ render ------------------------------ */

  if (page.kind === 'loading') {
    return (
      <PublicShell locale={locale}>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--lc-action-primary)]" aria-label={bt('loading', locale)} />
        </div>
      </PublicShell>
    )
  }

  if (page.kind === 'unavailable') {
    return (
      <PublicShell locale={locale}>
        <NoticePanel
          title={bt('unavailable.title', locale)}
          detail={bt('unavailable.detail', locale)}
        />
      </PublicShell>
    )
  }

  if (page.kind === 'error' || !readyMeta) {
    return (
      <PublicShell locale={locale}>
        <NoticePanel
          title={bt('error.generic.title', locale)}
          detail={bt('error.generic.detail', locale)}
        />
      </PublicShell>
    )
  }

  if (booked) {
    return (
      <PublicShell locale={locale}>
        <section
          className={cn(
            'rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)]',
            'bg-[var(--lc-status-published-bg)] p-[var(--lc-space-lg)]',
            'text-[var(--lc-status-published-fg)]',
            'shadow-[var(--lc-elevation-sm)]',
          )}
        >
          <div className="flex gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
            <div>
              <h1 className="font-semibold" style={{ font: 'var(--lc-type-heading-2)' }}>
                {bt('confirmed.title', locale)}
              </h1>
              <p className="mt-2 text-[length:var(--lc-type-body-sm)] opacity-90">
                {bt('confirmed.detail', locale)}
              </p>
              <p className="mt-4 text-[length:var(--lc-type-body-sm)]">
                <span className="font-medium">{bt('confirmed.when', locale)}: </span>
                {formatSlotRange(booked, readyMeta.timezone, locale)}
              </p>
              <p className="mt-2 text-[length:var(--lc-type-body-sm)] opacity-90">
                {bt('confirmed.close', locale)}
              </p>
            </div>
          </div>
        </section>
      </PublicShell>
    )
  }

  return (
    <PublicShell locale={locale}>
      {/* Header */}
      <header className="mb-6">
        <p className="mb-1 text-[length:var(--lc-type-body-sm)] text-[var(--lc-text-muted)]">
          {bt('header.with', locale)}
        </p>
        <h1 className="font-semibold text-[var(--lc-text-primary)]" style={{ font: 'var(--lc-type-heading-1)' }}>
          {readyMeta.owner_name}
        </h1>
        <p className="mt-1 text-[length:var(--lc-type-body-lg)] text-[var(--lc-text-muted)]">
          {readyMeta.agency_name}
        </p>
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-[var(--lc-radius-md)] bg-[var(--lc-surface-sunken)] px-3 py-1.5 text-[length:var(--lc-type-body-sm)] text-[var(--lc-text-muted)]">
          <Globe className="h-4 w-4 shrink-0" aria-hidden />
          {btf('timesInTimezone', locale, { timezone: readyMeta.timezone })}
        </p>
      </header>

      <section
        className={cn(
          'rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)]',
          'bg-[var(--lc-surface-raised)] p-[var(--lc-space-lg)]',
          'shadow-[var(--lc-elevation-sm)]',
        )}
      >
        {/* Duration toggle */}
        <div className="mb-6">
          <span className="mb-2 block text-sm font-medium text-[var(--lc-text-primary)]">
            {bt('duration.label', locale)}
          </span>
          <div className="inline-flex rounded-[var(--lc-radius-md)] bg-[var(--lc-surface-sunken)] p-1" role="group" aria-label={bt('duration.label', locale)}>
            {POSTABLE_DURATIONS.filter((d) => readyMeta.durations.includes(d)).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => pickDuration(d)}
                aria-pressed={duration === d}
                className={cn(
                  'min-h-tap rounded-[var(--lc-radius-sm)] px-4 text-sm font-medium transition-colors',
                  duration === d
                    ? 'bg-[var(--lc-surface)] text-[var(--lc-text-primary)] shadow-[var(--lc-elevation-sm)]'
                    : 'text-[var(--lc-text-muted)]',
                )}
              >
                {btf('duration.minutes', locale, { minutes: String(d) })}
              </button>
            ))}
          </div>
        </div>

        {/* Day strip */}
        <div className="mb-6">
          <span className="mb-2 block text-sm font-medium text-[var(--lc-text-primary)]">
            {bt('dayStrip.label', locale)}
          </span>
          <div className="flex gap-2 overflow-x-auto pb-2 [-webkit-overflow-scrolling:touch]" role="listbox" aria-label={bt('dayStrip.label', locale)}>
            {days.map((day) => {
              const selected = day.key === selectedDayKey
              return (
                <button
                  key={day.key}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  aria-label={formatFullDayLabel(day, readyMeta.timezone, locale)}
                  onClick={() => pickDay(day.key)}
                  className={cn(
                    'flex min-h-tap w-16 shrink-0 flex-col items-center justify-center gap-0.5 rounded-[var(--lc-radius-md)] border px-2 transition-colors',
                    selected
                      ? 'border-[var(--lc-action-primary)] bg-[var(--lc-action-primary)] text-[var(--lc-action-primary-text)]'
                      : 'border-[var(--lc-border)] bg-[var(--lc-surface)] text-[var(--lc-text-primary)]',
                  )}
                >
                  <span className="text-[length:var(--lc-type-body-sm)]">
                    {formatDayName(day, readyMeta.timezone, locale)}
                  </span>
                  <span className="text-base font-semibold tabular-nums">
                    {formatDayNumber(day, readyMeta.timezone, locale)}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Slots */}
        <div className="mb-6">
          <span className="mb-2 block text-sm font-medium text-[var(--lc-text-primary)]">
            {bt('slots.label', locale)}
          </span>
          {slots.kind === 'loading' ? (
            <div className="flex min-h-24 items-center justify-center py-6" role="status">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--lc-action-primary)]" aria-label={bt('slots.loading', locale)} />
            </div>
          ) : null}
          {slots.kind === 'error' ? (
            <p className="rounded-[var(--lc-radius-md)] bg-[var(--lc-status-unpublished-bg)] p-[var(--lc-space-md)] text-[length:var(--lc-type-body-sm)] text-[var(--lc-status-unpublished-fg)]" role="alert">
              {bt('slots.error', locale)}
            </p>
          ) : null}
          {slots.kind === 'ready' && slots.slots.length === 0 ? (
            <p className="rounded-[var(--lc-radius-md)] bg-[var(--lc-surface-sunken)] p-[var(--lc-space-md)] text-[length:var(--lc-type-body-sm)] text-[var(--lc-text-muted)]">
              {bt('slots.empty', locale)}
            </p>
          ) : null}
          {slots.kind === 'ready' && slots.slots.length > 0 ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" role="group" aria-label={bt('slots.label', locale)}>
              {slots.slots.map((slot) => {
                const selected = selectedSlot?.starts_at === slot.starts_at
                return (
                  <button
                    key={slot.starts_at}
                    type="button"
                    onClick={() => setSelectedSlot(slot)}
                    aria-pressed={selected}
                    className={cn(
                      'min-h-tap rounded-[var(--lc-radius-md)] border px-3 py-2 text-sm font-medium tabular-nums transition-colors',
                      selected
                        ? 'border-[var(--lc-action-primary)] bg-[var(--lc-action-primary)] text-[var(--lc-action-primary-text)]'
                        : 'border-[var(--lc-border)] bg-[var(--lc-surface)] text-[var(--lc-text-primary)] hover:border-[var(--lc-action-primary)]',
                    )}
                  >
                    {formatTime(slot.starts_at, readyMeta.timezone, locale)}
                  </button>
                )
              })}
            </div>
          ) : null}
        </div>

        {/* Visitor form */}
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
          className="space-y-4"
        >
          <h2 className="font-semibold text-[var(--lc-text-primary)]" style={{ font: 'var(--lc-type-heading-2)' }}>
            {bt('form.heading', locale)}
          </h2>

          <div className="space-y-1.5">
            <Label htmlFor="booking-name">{bt('form.name', locale)}</Label>
            <Input
              id="booking-name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-invalid={Boolean(fieldErrors.name)}
            />
            {fieldErrors.name ? (
              <p className="text-[length:var(--lc-type-body-sm)] text-[var(--lc-status-unpublished-fg)]" role="alert">
                {fieldErrors.name}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="booking-email">{bt('form.email', locale)}</Label>
            <Input
              id="booking-email"
              type="email"
              dir="ltr"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={Boolean(fieldErrors.email)}
            />
            {fieldErrors.email ? (
              <p className="text-[length:var(--lc-type-body-sm)] text-[var(--lc-status-unpublished-fg)]" role="alert">
                {fieldErrors.email}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="booking-phone">{bt('form.phone', locale)}</Label>
            <Input
              id="booking-phone"
              type="tel"
              dir="ltr"
              autoComplete="tel"
              placeholder="+971…"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              aria-invalid={Boolean(fieldErrors.phone)}
            />
            {fieldErrors.phone ? (
              <p className="text-[length:var(--lc-type-body-sm)] text-[var(--lc-status-unpublished-fg)]" role="alert">
                {fieldErrors.phone}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="booking-notes">{bt('form.notes', locale)}</Label>
            <textarea
              id="booking-notes"
              rows={3}
              maxLength={500}
              placeholder={bt('form.notes.placeholder', locale)}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={cn(
                'flex min-h-tap w-full rounded-md border border-[var(--lc-border-strong)] bg-[var(--lc-surface)]',
                'px-3 py-2 text-sm text-[var(--lc-text-primary)] placeholder:text-[var(--lc-text-muted)]',
                'focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              )}
            />
            {fieldErrors.notes ? (
              <p className="text-[length:var(--lc-type-body-sm)] text-[var(--lc-status-unpublished-fg)]" role="alert">
                {fieldErrors.notes}
              </p>
            ) : null}
          </div>

          {formError ? (
            <p
              className="rounded-[var(--lc-radius-md)] bg-[var(--lc-status-unpublished-bg)] p-[var(--lc-space-md)] text-[length:var(--lc-type-body-sm)] text-[var(--lc-status-unpublished-fg)]"
              role="alert"
            >
              {bt(formError.key, locale)}
            </p>
          ) : null}

          <Button type="submit" disabled={submitting || !selectedSlot} className="min-h-tap w-full">
            {submitting ? (
              <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <CalendarClock className="me-2 h-4 w-4" aria-hidden />
            )}
            {submitting ? bt('form.submitting', locale) : bt('form.submit', locale)}
          </Button>
        </form>
      </section>
    </PublicShell>
  )
}

/** Shared public-page chrome: full-height token background, centered column. */
function PublicShell({ children, locale }: { children: ReactNode; locale: BookingLocale }) {
  return (
    <div className="min-h-screen bg-[var(--lc-bg-page)] px-4 py-10" lang={locale} dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-6 flex items-center gap-2 text-[var(--lc-text-muted)]">
          <CalendarClock className="h-5 w-5" aria-hidden />
          <span className="text-sm font-medium tracking-wide">{bt('brand', locale)}</span>
        </div>
        {children}
      </div>
    </div>
  )
}

function NoticePanel({ title, detail }: { title: string; detail: string }) {
  return (
    <section
      role="alert"
      className={cn(
        'rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)]',
        'bg-[var(--lc-status-unpublished-bg)] p-[var(--lc-space-lg)]',
        'text-[var(--lc-status-unpublished-fg)]',
        'shadow-[var(--lc-elevation-sm)]',
      )}
    >
      <div className="flex gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
        <div>
          <h1 className="font-semibold" style={{ font: 'var(--lc-type-heading-2)' }}>
            {title}
          </h1>
          <p className="mt-2 text-[length:var(--lc-type-body-sm)] opacity-90">{detail}</p>
        </div>
      </div>
    </section>
  )
}

export default PublicBookingPage
